import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, ExternalLink, Copy, Link2, CreditCard, UserRound, Send, MailPlus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { catLabel, US_STATES, postedLabel, isFresh } from "../lib/constants";
import { OwnerEmailedTag, ownerAddress } from "./OwnerPitchDialog";
import { PlatformBadge, FreshBadge, SentTags, SignedTag } from "./LeadList";
import { LeadWork } from "./LeadWork";
import { SignedCelebration } from "./SignedCelebration";
import { DealStageTag, DealStageButtons, DealAgeTag } from "./DealStage";
import { WhereToNext } from "./WhereToNext";

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex gap-3 py-3 border-b border-slate-100" data-testid={testid}>
    <Icon className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="font-mono-label text-[10px] text-slate-400">{label}</p>
      <div className="text-sm text-slate-800 break-words">{children || <span className="text-slate-400">Not found</span>}</div>
    </div>
  </div>
);

const ext = (url) => url && (
  <a href={url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline inline-flex items-center gap-1 break-all">
    {url.replace(/^https?:\/\//, "").slice(0, 60)} <ExternalLink className="h-3 w-3" />
  </a>
);

export const LeadDrawer = ({ lead, onClose, team, onUpdate, readOnly, onMove, onEmailRep, isAdmin, onEmailOwner, onMarkOwner, userId, userName, onStage, onSeen, allLabel }) => {
  const address = lead && [lead.address, lead.city, lead.state, lead.zip].filter(Boolean).join(", ");
  const copy = () => {
    const text = [lead.name, lead.contact_name && `${lead.contact_name}${lead.contact_title ? ` (${lead.contact_title})` : ""}`, lead.contact_phone, lead.contact_email, address, lead.phone, lead.email, lead.website].filter(Boolean).join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Contact copied");
  };
  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto overflow-x-hidden bg-white" data-testid="lead-drawer">
        {lead && (
          <>
            <SheetHeader className="text-left">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">{catLabel(lead.category)}</span>
                <PlatformBadge platform={lead.source_platform} />
                {!lead.signal_date_approx && isFresh(lead.signal_date) && <FreshBadge testid="lead-drawer-fresh" />}
              </div>
              <SheetTitle className="font-display text-2xl font-extrabold tracking-tight pt-2" data-testid="lead-drawer-name">{lead.name}</SheetTitle>
              <SheetDescription>{US_STATES[lead.state]} · {postedLabel(lead.signal_date, lead.signal_date_approx)} · found {new Date(lead.found_at).toLocaleDateString()}</SheetDescription>
            </SheetHeader>
            <SignedCelebration lead={lead} userId={userId} onSeen={onSeen} />

            {(lead.owner_emailed_at || (lead.bucket === "signed" && lead.signed_at) || lead.bucket === "potential" || lead.sent_to_reps?.length > 0) && (
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="lead-drawer-tags">
                {lead.bucket === "signed" && lead.signed_at && <span className="flex items-center gap-2" data-testid="lead-drawer-signed"><SignedTag lead={lead} size="text-xs" /><span className="text-xs text-slate-500">{lead.potential_by ? `${lead.potential_by}'s deal` : ""}</span></span>}
                {lead.bucket === "potential" && <span className="flex flex-wrap gap-2" data-testid="lead-drawer-stage"><DealAgeTag lead={lead} size="text-xs" /><DealStageTag lead={lead} size="text-xs" /></span>}
                {lead.sent_to_reps?.length > 0 && <SentTags lead={lead} size="text-xs" />}
                {!readOnly && lead.owner_emailed_at && <span className="flex items-center gap-1.5" data-testid="lead-drawer-owner-emailed"><OwnerEmailedTag lead={lead} size="text-xs" />
                  {isAdmin && lead.owner_emailed_manual && <button onClick={() => onMarkOwner(lead, false)} className="text-xs font-semibold text-slate-500 hover:text-slate-900 underline" data-testid="lead-drawer-owner-mark-undo">Undo</button>}</span>}
              </div>
            )}
            {readOnly && !isAdmin && (lead.view_only || ["potential", "signed"].includes(lead.bucket)) && <p className="mt-4 text-xs font-semibold text-slate-500" data-testid="lead-drawer-view-only">View only · {["potential", "signed"].includes(lead.bucket) ? (lead[`${lead.bucket}_uid`] === userId ? "your deal" : `${lead.potential_by || "another rep"}'s deal`) : lead.bucket === "archived" ? `moved to Dead by ${lead.bucket_by || "another rep"}` : "another rep's lead"}</p>}

            {lead.move_notes?.length > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2.5" data-testid="lead-drawer-move-notes">
                <p className="font-mono-label text-[10px] text-amber-800">Notes from Jeff ({lead.move_notes.length})</p>
                {[...lead.move_notes].reverse().map((n, i) => (
                  <div key={n.at + i} className={i ? "pt-2.5 border-t border-amber-200" : ""} data-testid={`lead-move-note-${i}`}>
                    <p className="text-[11px] text-amber-900 font-semibold">{new Date(n.at).toLocaleDateString()}{n.rep ? ` · to ${n.rep}` : ""}</p>
                    <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{n.text}</p>
                  </div>
                ))}
              </div>
            )}
            {!readOnly && <WhereToNext lead={lead} isAdmin={isAdmin} myId={userId} allLabel={allLabel} onGo={(m) => onMove(lead, m)} />}

            {!readOnly && (
              <div className="mt-4 space-y-2" data-testid="lead-drawer-reach-out">
                <p className="font-mono-label text-[10px] text-slate-400">Reach out</p>
                <div className="grid grid-cols-2 gap-2">
                  {!lead.owner_emailed_at && ownerAddress(lead) ? (
                    <button onClick={() => onEmailOwner(lead)} data-testid="lead-drawer-email-owner"
                      className="h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"><MailPlus className="h-4 w-4" />Email owner</button>
                  ) : <p className="h-11 flex items-center justify-center text-xs text-slate-400 text-center" data-testid="lead-drawer-no-owner-email">{lead.owner_emailed_at ? "Owner already emailed" : "No owner email yet"}</p>}
                  {onEmailRep ? (
                    <button onClick={() => onEmailRep(lead)} data-testid="lead-drawer-email-rep"
                      className="h-11 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"><Send className="h-4 w-4" />Email rep</button>
                  ) : <span />}
                </div>
                {onStage && lead.bucket === "potential" && lead.potential_uid && <DealStageButtons lead={lead} onChanged={onStage} />}
                {isAdmin && !lead.owner_emailed_at && (
                  <button onClick={() => onMarkOwner(lead, true)} data-testid="lead-drawer-owner-mark" className="w-full text-xs font-semibold text-emerald-700 hover:underline">Mark as Sent Owner Email</button>
                )}
              </div>
            )}

            {lead.signal_quote && (
              <blockquote className="mt-5 p-4 rounded-xl bg-slate-50 border-l-4 border-blue-700 text-sm text-slate-700 italic" data-testid="lead-drawer-quote">
                "{lead.signal_quote}"
              </blockquote>
            )}

            <div className="mt-3">
              <Row icon={CreditCard} label="Current POS system" testid="lead-drawer-current-pos">{lead.current_pos}</Row>
              <Row icon={UserRound} label="Owner / manager" testid="lead-drawer-contact-name">
                {lead.contact_name && <>{lead.contact_name}{lead.contact_title && <span className="text-slate-500"> · {lead.contact_title}</span>}</>}
              </Row>
              {(lead.contact_phone || lead.contact_email) && (
                <Row icon={Phone} label="Owner / manager direct" testid="lead-drawer-contact-direct">
                  {lead.contact_phone && <a href={`tel:${lead.contact_phone}`} className="text-blue-700 block">{lead.contact_phone}</a>}
                  {lead.contact_email && <a href={`mailto:${lead.contact_email}`} className="text-blue-700 block">{lead.contact_email}</a>}
                </Row>
              )}
              <Row icon={MapPin} label={lead.location_precise ? "Address" : "Address (pin approximate)"} testid="lead-drawer-address">{address}</Row>
              <Row icon={Phone} label="Phone" testid="lead-drawer-phone">{lead.phone && <a href={`tel:${lead.phone}`} className="text-blue-700">{lead.phone}</a>}</Row>
              <Row icon={Mail} label="Email" testid="lead-drawer-email">{lead.email && <a href={`mailto:${lead.email}`} className="text-blue-700">{lead.email}</a>}</Row>
              <Row icon={Globe} label="Website" testid="lead-drawer-website">{ext(lead.website)}</Row>
              <Row icon={Link2} label="Social profile" testid="lead-drawer-social">{ext(lead.social_url)}</Row>
              <Row icon={ExternalLink} label="Where we found the signal" testid="lead-drawer-source">
                {ext(lead.source_url)}
                <span className="block text-xs text-slate-500 mt-0.5" data-testid="lead-drawer-posted">{postedLabel(lead.signal_date, lead.signal_date_approx)}{lead.date_note && ` · date from ${lead.date_note}`}</span>
              </Row>
            </div>

            {!readOnly && <LeadWork key={lead.id} lead={lead} team={team} onUpdate={onUpdate} isAdmin={isAdmin} userId={userId} userName={userName} />}

            <div className="mt-6">
              <Button variant="outline" className="w-full rounded-full" onClick={copy} data-testid="lead-copy-contact"><Copy className="h-4 w-4 mr-1.5" />Copy contact</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
